/**
 * AssetsView — the headline asset-binding workspace.
 *
 * Makes assets REAL: every symbol a scene references (from `parseSceneSymbols`)
 * is shown with its bind state against an `AssetMapping`, and binding an asset
 * lets the preview resolve it. Built entirely from the cream design-system
 * primitives — no hardcoded hex, only `var(--…)` tokens. Fully bilingual via
 * `useTr` + a local `S` string table (+ shared `COMMON`).
 *
 * Layout (top → bottom):
 *  - a coverage header: 「已绑定 N / M · 缺失 K」 + a rose progress bar.
 *  - grouped sections — 背景 (backgrounds), 角色 (characters, each look a
 *    sub-row), CG, 音乐 (music) and 音效 (sfx). Visual symbols can be uploaded,
 *    picked from the library, or AI-generated. Audio symbols (music / sfx) can
 *    ONLY be uploaded or picked — audio can't be AI-generated — so they carry no
 *    generate affordance and show a filename chip + inline <audio> when bound.
 *  - a general uploaded-asset library grid.
 *
 * Purely presentational and prop-driven: it never fetches. The container
 * (App.tsx) owns the mapping, the asset list, thumbnail object URLs, and the
 * upload / bind / unbind / generate side effects.
 */

import {
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  Image as ImageIcon,
  Users,
  Sparkles,
  TriangleAlert,
  Upload,
  Library,
  Wand2,
  Unlink,
  Check,
  Music,
  Volume2,
  FileAudio,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  Dropzone,
  EmptyState,
  ScrollArea,
  Spinner,
  Textarea,
} from "../../ui/index.js";
import {
  coverage,
  type AssetKind,
  type AssetMapping,
} from "../../assets/mapping.js";
import type { StyleMeta } from "../../api/client.js";
import type { ParsedSymbols } from "../../assets/symbols.js";
import { classifyAsset, extensionLabel } from "../../assets/classify.js";
import { useTr, type Entry } from "../../i18n/context.js";
import { COMMON } from "../../i18n/common.js";
import "./AssetsView.css";

/** View-local bilingual copy. Shared words reuse `COMMON`. */
const S = {
  title: { zh: "素材绑定", en: "Asset binding" },
  subtitle: {
    zh: "剧本里出现的每个画面和声音，都要绑定一份素材，预览才能用上。",
    en: "Every visual and sound the script names needs a bound asset before preview can use it.",
  },
  allBound: { zh: "已全部绑定", en: "All bound" },
  missing: { zh: "缺失 {n}", en: "{n} missing" },
  boundCount: { zh: "已绑定 {bound} / {total}", en: "Bound {bound} / {total}" },
  progressLabel: { zh: "素材绑定进度：{bound} / {total}", en: "Binding progress: {bound} / {total}" },

  // section headings
  backgrounds: { zh: "背景", en: "Backgrounds" },
  characters: { zh: "角色", en: "Characters" },
  cg: { zh: "CG", en: "CG" },
  music: { zh: "音乐", en: "Music" },
  sfx: { zh: "音效", en: "SFX" },
  looksCount: { zh: "{n} 个表情", en: "{n} looks" },

  // empty scene
  emptyTitle: { zh: "这一章还没有画面", en: "No scenes here yet" },
  emptyDesc: {
    zh: "先在剧本里写下背景、角色、CG 或音乐音效，这里就会列出需要绑定的素材。",
    en: "Write a background, character, CG, or music/sfx in the script and it will appear here to bind.",
  },

  // library
  library: { zh: "素材库", en: "Library" },
  libraryEmpty: {
    zh: "素材库还是空的。上传后，就能在上方任意画面或声音里「选择已有素材」。",
    en: "The library is empty. Upload assets to reuse them via “Choose existing” above.",
  },
  dropImages: { zh: "拖拽图片或音频到这里，或点击上传", en: "Drop images or audio here, or click to upload" },
  dropImagesHint: { zh: "PNG · JPG · WEBP · GIF · SVG · MP3 · WAV · OGG · M4A", en: "PNG · JPG · WEBP · GIF · SVG · MP3 · WAV · OGG · M4A" },
  dropUploading: { zh: "上传中…", en: "Uploading…" },
  uploadToLibrary: { zh: "上传素材到素材库", en: "Upload assets to the library" },

  // rows
  bound: { zh: "已绑定", en: "Bound" },
  unbound: { zh: "未绑定", en: "Not bound" },
  notBoundYet: { zh: "尚未绑定", en: "Not bound yet" },
  replace: { zh: "替换", en: "Replace" },
  unbind: { zh: "解绑", en: "Unbind" },
  chooseExisting: { zh: "选择已有素材", en: "Choose existing" },
  genNote: { zh: "AI 生成暂不可用", en: "AI generation unavailable" },
  genNoteTitle: {
    zh: "需要登录账号，且服务端已配置图像生成能力",
    en: "Requires a signed-in account and configured image generation service",
  },
  uploadImageAria: { zh: "{label}图片", en: "{label} image" },
  uploadAudioAria: { zh: "{label}音频", en: "{label} audio" },

  // picker dialog
  pickTitle: { zh: "选择已有素材", en: "Choose an existing asset" },
  pickDescImage: { zh: "为「{symbol}」绑定一张素材库里的图片。", en: "Bind an image from the library to “{symbol}”." },
  pickDescAudio: { zh: "为「{symbol}」绑定一段素材库里的音频。", en: "Bind an audio clip from the library to “{symbol}”." },
  pickEmptyImageTitle: { zh: "素材库里还没有图片", en: "No images in the library yet" },
  pickEmptyImageDesc: { zh: "先在下方上传一些立绘、背景或 CG，再来选择。", en: "Upload some sprites, backgrounds, or CG below first." },
  pickEmptyAudioTitle: { zh: "素材库里还没有音频", en: "No audio in the library yet" },
  pickEmptyAudioDesc: { zh: "先在下方上传一些音乐或音效，再来选择。", en: "Upload some music or sfx below first." },

  // generate dialog
  genTitle: { zh: "生成素材", en: "Generate asset" },
  genDesc: { zh: "为「{symbol}」选择画风、描述画面，即可用 AI 生成。", en: "Pick a style and describe the shot to generate “{symbol}” with AI." },
  genStart: { zh: "开始生成", en: "Start" },
  genStyleLabel: { zh: "画风（风格）", en: "Style" },
  genStyleAria: { zh: "画风", en: "Style" },
  genPromptLabel: { zh: "画面描述", en: "Description" },
  genPromptHint: { zh: "只需描述画什么，画风会套用你选的风格模板。", en: "Just describe what to draw; the chosen style template handles the rest." },
  genAiWrite: { zh: "AI 帮我写提示词", en: "Let AI write the prompt" },
  genAiWriting: { zh: "AI 撰写中…", en: "AI is writing…" },
  genAiHint: { zh: "AI 会根据画风和名称起草提示词，填入下方供你修改后再生成。", en: "AI drafts a prompt from the style + name; review and edit it below before generating." },
  genAiError: { zh: "提示词生成失败，请重试或手动填写。", en: "Couldn’t draft a prompt — try again or write it yourself." },

  // busy
  busy: { zh: "处理中…", en: "Working…" },
} satisfies Record<string, Entry>;

/** Image extensions the visual binding picker / upload accepts. */
const IMAGE_ACCEPT = [".png", ".jpg", ".jpeg", ".webp", ".gif", ".svg"] as const;
/** Audio extensions the music / sfx picker / upload accepts (audio can't be generated). */
const AUDIO_ACCEPT = [".mp3", ".wav", ".ogg", ".m4a"] as const;

/** True for the audio families (music / sfx) — upload-only, no AI generation. */
function isAudioKind(kind: AssetKind): boolean {
  return kind === "music" || kind === "sfx";
}

/** True when an asset id looks like an image. */
function isImage(id: string): boolean {
  return classifyAsset(id) === "image";
}

/** True when an asset id looks like audio. */
function isAudio(id: string): boolean {
  return classifyAsset(id) === "audio";
}

/** Whether an asset id is bindable to the given kind (audio→audio, else image). */
function isBindableTo(id: string, kind: AssetKind): boolean {
  return isAudioKind(kind) ? isAudio(id) : isImage(id);
}

/** Resolve the bound assetId for a symbol within the mapping, if any. */
function boundAssetId(
  mapping: AssetMapping,
  kind: AssetKind,
  symbol: string,
  charId?: string,
): string | undefined {
  if (kind === "background") {
    return mapping.backgrounds.find((b) => b.symbol === symbol)?.assetId || undefined;
  }
  if (kind === "cg") {
    return mapping.cg.find((b) => b.symbol === symbol)?.assetId || undefined;
  }
  if (kind === "music") {
    return mapping.music.find((b) => b.symbol === symbol)?.assetId || undefined;
  }
  if (kind === "sfx") {
    return mapping.sfx.find((b) => b.symbol === symbol)?.assetId || undefined;
  }
  const looks = mapping.characters.find((c) => c.id === charId)?.looks ?? [];
  return looks.find((l) => l.symbol === symbol)?.assetId || undefined;
}

/**
 * A subject-focused default prompt seeded from the symbol + kind. The selected
 * art style (风格) template supplies all the rendering direction, so this only
 * describes WHAT to draw. Audio kinds never reach this (no generate flow).
 */
export function seedPrompt(target: SymbolTarget): string {
  const name = target.symbol.replace(/[_-]+/g, " ");
  switch (target.kind) {
    case "background":
      return `${name}, a warm soft visual-novel background, no characters`;
    case "character": {
      const who = target.charId ?? "character";
      return `${who}, expression and pose: ${name}`;
    }
    case "cg":
      return `${name}, an emotional key CG illustration for a visual novel`;
    default:
      return name;
  }
}

/** A library asset the workspace can bind to or display. */
export interface AssetsViewAsset {
  readonly id: string;
  /** Authed object URL for image / audio assets (resolved by the container). */
  readonly thumbUrl?: string;
}

/** Identifies a single bindable symbol target. */
export interface SymbolTarget {
  readonly kind: AssetKind;
  readonly symbol: string;
  /** Present only for `kind === "character"`. */
  readonly charId?: string;
}

export interface AssetsViewProps {
  /** Symbols the active scene references (from `parseSceneSymbols`). */
  readonly parsed: ParsedSymbols;
  /** The per-episode binding table. */
  readonly mapping: AssetMapping;
  /** The project's uploaded asset library. */
  readonly assets: readonly AssetsViewAsset[];
  /** Runtime capabilities — gates the AI-generation affordance (images only). */
  readonly capabilities: { readonly imageGen: boolean };
  /** The art-style catalog (风格选择) for the generate dialog. */
  readonly styles?: readonly StyleMeta[];
  /** Disables actions while a mutation is in flight. */
  readonly busy?: boolean;
  /** Upload file(s); when `bind` is given, the first matching file is bound to it. */
  readonly onUpload: (files: File[], bind?: SymbolTarget) => void;
  /** Bind an existing library asset to a symbol. */
  readonly onBind: (
    kind: AssetKind,
    symbol: string,
    assetId: string,
    charId?: string,
  ) => void;
  /** Remove a symbol's binding. */
  readonly onUnbind: (kind: AssetKind, symbol: string, charId?: string) => void;
  /** Generate art for a symbol from a prompt + selected art style (风格). Images only. */
  readonly onGenerate: (args: {
    kind: AssetKind;
    symbol: string;
    charId?: string;
    prompt: string;
    family?: string;
  }) => void;
  /**
   * Ask the AI to draft an image prompt for review (never auto-generates). When
   * provided, the generate dialog shows an "AI 帮我写提示词" affordance that fills
   * the description field; the user still triggers generation explicitly.
   */
  readonly onSuggestPrompt?: (args: {
    kind: AssetKind;
    name: string;
    styleName?: string;
    styleDescription?: string;
    notes?: string;
  }) => Promise<string>;
}

// ── coverage header ─────────────────────────────────────────────────────────

function CoverageHeader({
  bound,
  total,
  missing,
}: {
  bound: number;
  total: number;
  missing: number;
}): ReactElement {
  const tr = useTr();
  const pct = total === 0 ? 0 : Math.round((bound / total) * 100);
  const complete = total > 0 && missing === 0;
  return (
    <header className="lw-assets__header">
      <div className="lw-assets__header-top">
        <div className="lw-assets__title-group">
          <h2 className="lw-assets__title">{tr(S.title)}</h2>
          <p className="lw-assets__subtitle">{tr(S.subtitle)}</p>
        </div>
        <div className="lw-assets__coverage" role="status" aria-live="polite">
          {complete ? (
            <Badge tone="success" variant="tonal" size="md">
              <Check size={13} aria-hidden="true" /> {tr(S.allBound)}
            </Badge>
          ) : (
            <Badge tone={missing > 0 ? "rose" : "neutral"} variant="tonal" size="md">
              {tr(S.missing, { n: missing })}
            </Badge>
          )}
          <span className="lw-assets__coverage-count">
            {tr(S.boundCount, { bound, total })}
          </span>
        </div>
      </div>
      <div
        className="lw-assets__progress"
        role="progressbar"
        aria-valuenow={bound}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={tr(S.progressLabel, { bound, total })}
      >
        <span
          className="lw-assets__progress-fill"
          data-complete={complete || undefined}
          style={{ width: `${pct}%` }}
        />
      </div>
    </header>
  );
}

// ── pick-from-library dialog ──────────────────────────────────────────────────

function PickerDialog({
  open,
  onOpenChange,
  target,
  assets,
  busy,
  onPick,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  target: SymbolTarget | null;
  assets: readonly AssetsViewAsset[];
  busy: boolean;
  onPick: (assetId: string) => void;
}): ReactElement {
  const tr = useTr();
  const audio = target ? isAudioKind(target.kind) : false;
  const candidates = useMemo(
    () => (target ? assets.filter((a) => isBindableTo(a.id, target.kind)) : []),
    [assets, target],
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={tr(S.pickTitle)}
      description={
        target
          ? tr(audio ? S.pickDescAudio : S.pickDescImage, { symbol: target.symbol })
          : undefined
      }
    >
      {candidates.length === 0 ? (
        <EmptyState
          icon={
            audio ? (
              <FileAudio size={22} aria-hidden="true" />
            ) : (
              <Library size={22} aria-hidden="true" />
            )
          }
          title={tr(audio ? S.pickEmptyAudioTitle : S.pickEmptyImageTitle)}
          description={tr(audio ? S.pickEmptyAudioDesc : S.pickEmptyImageDesc)}
        />
      ) : (
        <ScrollArea className="lw-assets__picker-scroll">
          <ul className="lw-assets__picker-grid">
            {candidates.map((asset) => (
              <li key={asset.id}>
                <button
                  type="button"
                  className="lw-assets__picker-item"
                  disabled={busy}
                  title={asset.id}
                  onClick={() => onPick(asset.id)}
                >
                  <span className="lw-assets__picker-thumb">
                    {audio ? (
                      <FileAudio size={20} aria-hidden="true" />
                    ) : asset.thumbUrl ? (
                      <img src={asset.thumbUrl} alt={asset.id} loading="lazy" />
                    ) : (
                      <ImageIcon size={20} aria-hidden="true" />
                    )}
                  </span>
                  <span className="lw-assets__picker-name">{asset.id}</span>
                </button>
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}
    </Dialog>
  );
}

// ── generate dialog (images only) ────────────────────────────────────────────

/**
 * Which style families support the given asset kind. Only the visual kinds
 * (background / character / cg) are ever generated — audio has no style catalog —
 * so `s.kinds` (a `WebAssetKind[]`) is compared as strings to keep audio kinds out.
 */
function stylesForKind(styles: readonly StyleMeta[], kind: AssetKind): StyleMeta[] {
  return styles.filter((s) => (s.kinds as readonly string[]).includes(kind));
}

/** The 风格选择 grid — selectable family cards with a preview thumbnail. */
function StylePicker({
  styles,
  value,
  onChange,
  disabled,
}: {
  styles: readonly StyleMeta[];
  value: string;
  onChange: (id: string) => void;
  disabled: boolean;
}): ReactElement {
  const tr = useTr();
  return (
    <div className="lw-gen__styles" role="radiogroup" aria-label={tr(S.genStyleAria)}>
      {styles.map((s) => {
        const active = s.id === value;
        return (
          <button
            key={s.id}
            type="button"
            role="radio"
            aria-checked={active}
            className="lw-gen__style"
            data-active={active || undefined}
            disabled={disabled}
            title={s.description}
            onClick={() => onChange(s.id)}
          >
            <span className="lw-gen__style-thumb">
              {s.previewUrl ? (
                <img src={s.previewUrl} alt={s.name} loading="lazy" />
              ) : (
                <ImageIcon size={18} aria-hidden="true" />
              )}
              {active ? (
                <span className="lw-gen__style-check" aria-hidden="true">
                  <Check size={12} />
                </span>
              ) : null}
            </span>
            <span className="lw-gen__style-name">{s.name}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Prompt-field header: the "画面描述" label + an optional "AI 帮我写提示词" action.
 * Pure + presentational (no Radix portal) so it can be unit-tested via SSR;
 * GenerateDialog wires the callbacks. Rendering the AI action is gated on `aiLabel`.
 */
export function PromptFieldHeader({
  label,
  aiLabel,
  onSuggest,
  suggesting = false,
  disabled = false,
}: {
  label: string;
  aiLabel?: string;
  onSuggest?: () => void;
  suggesting?: boolean;
  disabled?: boolean;
}): ReactElement {
  return (
    <div className="lw-gen__prompt-head">
      <span className="lw-gen__label">{label}</span>
      {aiLabel && onSuggest ? (
        <Button
          variant="ghost"
          size="sm"
          loading={suggesting}
          disabled={disabled}
          leadingIcon={<Sparkles size={14} aria-hidden="true" />}
          onClick={onSuggest}
        >
          {aiLabel}
        </Button>
      ) : null}
    </div>
  );
}

function GenerateDialog({
  open,
  onOpenChange,
  target,
  styles,
  busy,
  onConfirm,
  onSuggestPrompt,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  target: SymbolTarget | null;
  styles: readonly StyleMeta[];
  busy: boolean;
  onConfirm: (prompt: string, family: string | undefined) => void;
  onSuggestPrompt?: (args: {
    kind: AssetKind;
    name: string;
    styleName?: string;
    styleDescription?: string;
    notes?: string;
  }) => Promise<string>;
}): ReactElement {
  const tr = useTr();
  const seeded = target ? seedPrompt(target) : "";
  const kindStyles = target ? stylesForKind(styles, target.kind) : [];
  // Re-seed whenever the target changes by keying the inner field on the symbol.
  const fieldKey = target ? `${target.kind}:${target.charId ?? ""}:${target.symbol}` : "none";
  const [prompt, setPrompt] = useState(seeded);
  const [family, setFamily] = useState<string>(kindStyles[0]?.id ?? "");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(false);

  // Keep the textarea + style in sync with a freshly-opened target.
  const [lastKey, setLastKey] = useState(fieldKey);
  if (fieldKey !== lastKey) {
    setLastKey(fieldKey);
    setPrompt(seeded);
    setSuggestError(false);
    if (!kindStyles.some((s) => s.id === family)) setFamily(kindStyles[0]?.id ?? "");
  }

  const suggest = async (): Promise<void> => {
    if (!target || !onSuggestPrompt || suggesting || busy) return;
    setSuggesting(true);
    setSuggestError(false);
    try {
      const style = kindStyles.find((s) => s.id === family);
      const drafted = (
        await onSuggestPrompt({
          kind: target.kind,
          name: target.symbol,
          styleName: style?.name,
          styleDescription: style?.description,
          notes: prompt.trim() || undefined,
        })
      ).trim();
      // The user reviews/edits this before generating — we never auto-generate.
      if (drafted.length > 0) setPrompt(drafted);
      else setSuggestError(true);
    } catch {
      setSuggestError(true);
    } finally {
      setSuggesting(false);
    }
  };

  const canSubmit = prompt.trim().length > 0 && !busy && !suggesting;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={tr(S.genTitle)}
      description={target ? tr(S.genDesc, { symbol: target.symbol }) : undefined}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>
            {tr(COMMON.cancel)}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!canSubmit}
            leadingIcon={<Wand2 size={15} aria-hidden="true" />}
            onClick={() => canSubmit && onConfirm(prompt.trim(), family || undefined)}
          >
            {tr(S.genStart)}
          </Button>
        </>
      }
    >
      {kindStyles.length > 0 ? (
        <div className="lw-gen__field">
          <span className="lw-gen__label">{tr(S.genStyleLabel)}</span>
          <StylePicker styles={kindStyles} value={family} onChange={setFamily} disabled={busy} />
        </div>
      ) : null}
      <div className="lw-gen__field">
        <PromptFieldHeader
          label={tr(S.genPromptLabel)}
          aiLabel={onSuggestPrompt ? tr(suggesting ? S.genAiWriting : S.genAiWrite) : undefined}
          onSuggest={onSuggestPrompt ? () => void suggest() : undefined}
          suggesting={suggesting}
          disabled={busy || suggesting}
        />
        <Textarea
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          hint={tr(suggestError ? S.genAiError : onSuggestPrompt ? S.genAiHint : S.genPromptHint)}
          aria-label={tr(S.genPromptLabel)}
        />
      </div>
    </Dialog>
  );
}

// ── inline upload control ────────────────────────────────────────────────────

/** A compact inline upload control (label-as-button over a hidden file input). */
function ReplaceUpload({
  busy,
  onUpload,
  label,
  audio,
}: {
  busy: boolean;
  onUpload: (files: File[]) => void;
  label: string;
  /** When true, accept audio files instead of images. */
  audio: boolean;
}): ReactElement {
  const tr = useTr();
  const accept = (audio ? AUDIO_ACCEPT : IMAGE_ACCEPT).join(",");
  const ariaLabel = tr(audio ? S.uploadAudioAria : S.uploadImageAria, { label });
  return (
    <label className="lw-assets__inline-upload" data-busy={busy || undefined}>
      <span className="lc-btn lc-btn--secondary lc-btn--sm" aria-hidden="true">
        <span className="lc-btn__icon">
          <Upload size={14} aria-hidden="true" />
        </span>
        <span className="lc-btn__label">{label}</span>
      </span>
      <input
        type="file"
        accept={accept}
        disabled={busy}
        aria-label={ariaLabel}
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : [];
          e.target.value = "";
          if (files.length > 0) onUpload(files);
        }}
      />
    </label>
  );
}

// ── section wrapper ───────────────────────────────────────────────────────────

function Section({
  icon,
  title,
  count,
  children,
}: {
  icon: ReactNode;
  title: string;
  count: number;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="lw-assets__section">
      <h3 className="lw-assets__section-title">
        <span className="lw-assets__section-icon" aria-hidden="true">
          {icon}
        </span>
        {title}
        <span className="lw-assets__section-count">{count}</span>
      </h3>
      {children}
    </section>
  );
}

// ── main view ─────────────────────────────────────────────────────────────────

export function AssetsView(props: AssetsViewProps): ReactElement {
  const {
    parsed,
    mapping,
    assets,
    capabilities,
    styles = [],
    busy = false,
    onUpload,
    onBind,
    onUnbind,
    onGenerate,
    onSuggestPrompt,
  } = props;

  const tr = useTr();
  const cov = useMemo(() => coverage(mapping, parsed), [mapping, parsed]);

  // Map assetId → resolved (thumbnail / audio) URL for quick row lookup.
  const urlById = useMemo(() => {
    const m = new Map<string, string | undefined>();
    for (const a of assets) m.set(a.id, a.thumbUrl);
    return m;
  }, [assets]);

  // Dialog state shared by the picker / generate flows.
  const [pickerTarget, setPickerTarget] = useState<SymbolTarget | null>(null);
  const [genTarget, setGenTarget] = useState<SymbolTarget | null>(null);

  const resolveBoundId = (target: SymbolTarget): string | undefined =>
    boundAssetId(mapping, target.kind, target.symbol, target.charId);

  const hasSymbols =
    parsed.backgrounds.length > 0 ||
    parsed.characters.length > 0 ||
    parsed.cg.length > 0 ||
    parsed.music.length > 0 ||
    parsed.sfx.length > 0;

  const renderRow = (target: SymbolTarget, label: string): ReactElement => {
    const id = resolveBoundId(target);
    return (
      <SymbolRow
        key={`${target.kind}:${target.charId ?? ""}:${target.symbol}`}
        target={target}
        label={label}
        boundId={id}
        boundUrl={id ? urlById.get(id) : undefined}
        capabilities={capabilities}
        busy={busy}
        onUpload={(files) => onUpload(files, target)}
        onUnbind={() => onUnbind(target.kind, target.symbol, target.charId)}
        onOpenPicker={() => setPickerTarget(target)}
        onOpenGenerate={() => setGenTarget(target)}
      />
    );
  };

  return (
    <section className="lw-assets" aria-label={tr(S.title)}>
      <CoverageHeader bound={cov.bound} total={cov.total} missing={cov.missing.length} />

      <ScrollArea className="lw-assets__scroll">
        <div className="lw-assets__body">
          {!hasSymbols ? (
            <EmptyState
              icon={<Sparkles size={22} aria-hidden="true" />}
              title={tr(S.emptyTitle)}
              description={tr(S.emptyDesc)}
            />
          ) : (
            <>
              {parsed.backgrounds.length > 0 ? (
                <Section
                  icon={<ImageIcon size={16} aria-hidden="true" />}
                  title={tr(S.backgrounds)}
                  count={parsed.backgrounds.length}
                >
                  <ul className="lw-assets__rows">
                    {parsed.backgrounds.map((symbol) =>
                      renderRow({ kind: "background", symbol }, symbol),
                    )}
                  </ul>
                </Section>
              ) : null}

              {parsed.characters.length > 0 ? (
                <Section
                  icon={<Users size={16} aria-hidden="true" />}
                  title={tr(S.characters)}
                  count={parsed.characters.reduce((n, c) => n + c.looks.length, 0)}
                >
                  {parsed.characters.map((char) => (
                    <div key={char.id} className="lw-assets__char">
                      <div className="lw-assets__char-head">
                        <span className="lw-assets__char-name">{char.id}</span>
                        <Badge kind="character" variant="tonal" size="sm">
                          {tr(S.looksCount, { n: char.looks.length })}
                        </Badge>
                      </div>
                      <ul className="lw-assets__rows lw-assets__rows--sub">
                        {char.looks.map((look) =>
                          renderRow(
                            { kind: "character", symbol: look, charId: char.id },
                            look,
                          ),
                        )}
                      </ul>
                    </div>
                  ))}
                </Section>
              ) : null}

              {parsed.cg.length > 0 ? (
                <Section
                  icon={<Sparkles size={16} aria-hidden="true" />}
                  title={tr(S.cg)}
                  count={parsed.cg.length}
                >
                  <ul className="lw-assets__rows">
                    {parsed.cg.map((symbol) => renderRow({ kind: "cg", symbol }, symbol))}
                  </ul>
                </Section>
              ) : null}

              {parsed.music.length > 0 ? (
                <Section
                  icon={<Music size={16} aria-hidden="true" />}
                  title={tr(S.music)}
                  count={parsed.music.length}
                >
                  <ul className="lw-assets__rows">
                    {parsed.music.map((symbol) => renderRow({ kind: "music", symbol }, symbol))}
                  </ul>
                </Section>
              ) : null}

              {parsed.sfx.length > 0 ? (
                <Section
                  icon={<Volume2 size={16} aria-hidden="true" />}
                  title={tr(S.sfx)}
                  count={parsed.sfx.length}
                >
                  <ul className="lw-assets__rows">
                    {parsed.sfx.map((symbol) => renderRow({ kind: "sfx", symbol }, symbol))}
                  </ul>
                </Section>
              ) : null}
            </>
          )}

          {/* general uploaded-asset library */}
          <section className="lw-assets__section lw-assets__library">
            <h3 className="lw-assets__section-title">
              <span className="lw-assets__section-icon" aria-hidden="true">
                <Library size={16} aria-hidden="true" />
              </span>
              {tr(S.library)}
              <span className="lw-assets__section-count">{assets.length}</span>
            </h3>
            <Dropzone
              accept={[...IMAGE_ACCEPT, ...AUDIO_ACCEPT]}
              multiple
              disabled={busy}
              aria-label={tr(S.uploadToLibrary)}
              title={busy ? tr(S.dropUploading) : tr(S.dropImages)}
              hint={tr(S.dropImagesHint)}
              onFiles={(files) => onUpload(files)}
            />
            {assets.length === 0 ? (
              <p className="lw-assets__library-empty">{tr(S.libraryEmpty)}</p>
            ) : (
              <ul className="lw-assets__library-grid">
                {assets.map((asset) => {
                  const ext = extensionLabel(asset.id);
                  const img = isImage(asset.id);
                  const aud = isAudio(asset.id);
                  return (
                    <li key={asset.id} className="lw-assets__library-card">
                      <span className="lw-assets__library-thumb">
                        {img && asset.thumbUrl ? (
                          <img src={asset.thumbUrl} alt={asset.id} loading="lazy" />
                        ) : (
                          <span className="lw-assets__library-glyph">
                            {aud ? (
                              <FileAudio size={18} aria-hidden="true" />
                            ) : (
                              <ImageIcon size={18} aria-hidden="true" />
                            )}
                            {ext ? <em>{ext}</em> : null}
                          </span>
                        )}
                      </span>
                      <span className="lw-assets__library-name" title={asset.id}>
                        {asset.id}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </ScrollArea>

      {busy ? (
        <div className="lw-assets__busy" role="status" aria-live="polite">
          <Spinner size="sm" /> {tr(S.busy)}
        </div>
      ) : null}

      <PickerDialog
        open={pickerTarget !== null}
        onOpenChange={(v) => {
          if (!v) setPickerTarget(null);
        }}
        target={pickerTarget}
        assets={assets}
        busy={busy}
        onPick={(assetId) => {
          if (pickerTarget) {
            onBind(pickerTarget.kind, pickerTarget.symbol, assetId, pickerTarget.charId);
          }
          setPickerTarget(null);
        }}
      />

      <GenerateDialog
        open={genTarget !== null}
        onOpenChange={(v) => {
          if (!v) setGenTarget(null);
        }}
        target={genTarget}
        styles={styles}
        busy={busy}
        onSuggestPrompt={onSuggestPrompt}
        onConfirm={(prompt, family) => {
          if (genTarget) {
            onGenerate({
              kind: genTarget.kind,
              symbol: genTarget.symbol,
              charId: genTarget.charId,
              prompt,
              family,
            });
          }
          setGenTarget(null);
        }}
      />
    </section>
  );
}

/**
 * SymbolRow — renders one bindable symbol in either its bound (thumbnail /
 * filename chip + 解绑/替换) or unbound (⚠ + 上传/选择/生成) shape.
 *
 * Visual symbols show an image thumbnail; audio symbols (music / sfx) show a
 * filename chip + inline <audio> control instead, and never offer AI generation.
 * A bound-but-unresolved asset still reads as bound.
 */
function SymbolRow({
  target,
  label,
  boundId,
  boundUrl,
  capabilities,
  busy,
  onUpload,
  onUnbind,
  onOpenPicker,
  onOpenGenerate,
}: {
  target: SymbolTarget;
  label: string;
  boundId: string | undefined;
  boundUrl: string | undefined;
  capabilities: { imageGen: boolean };
  busy: boolean;
  onUpload: (files: File[]) => void;
  onUnbind: () => void;
  onOpenPicker: () => void;
  onOpenGenerate: () => void;
}): ReactElement {
  const tr = useTr();
  const bound = boundId !== undefined;
  const audio = isAudioKind(target.kind);
  return (
    <li className="lw-assets__row" data-bound={bound || undefined}>
      <div className="lw-assets__row-thumb">
        {bound && !audio && boundUrl ? (
          <img src={boundUrl} alt={label} loading="lazy" />
        ) : bound ? (
          <span className="lw-assets__row-glyph" aria-hidden="true">
            {audio ? <FileAudio size={16} /> : <ImageIcon size={16} />}
          </span>
        ) : (
          <span className="lw-assets__row-warn" title={tr(S.notBoundYet)}>
            <TriangleAlert size={16} aria-hidden="true" />
          </span>
        )}
      </div>
      <div className="lw-assets__row-meta">
        <span className="lw-assets__row-name">{label}</span>
        {bound ? (
          <Badge tone="success" variant="outline" size="sm" dot>
            {tr(S.bound)}
          </Badge>
        ) : (
          <Badge tone="rose" variant="outline" size="sm" dot>
            {tr(S.unbound)}
          </Badge>
        )}
        {bound && audio ? (
          <span className="lw-assets__row-audio">
            <span className="lw-assets__row-audio-name" title={boundId}>
              {boundId}
            </span>
            {boundUrl ? (
              <audio className="lw-assets__row-audio-player" src={boundUrl} controls preload="none" />
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="lw-assets__row-actions">
        {bound ? (
          <>
            <ReplaceUpload busy={busy} onUpload={onUpload} label={tr(S.replace)} audio={audio} />
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              leadingIcon={<Unlink size={14} aria-hidden="true" />}
              onClick={onUnbind}
            >
              {tr(S.unbind)}
            </Button>
          </>
        ) : (
          <>
            <ReplaceUpload busy={busy} onUpload={onUpload} label={tr(COMMON.upload)} audio={audio} />
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              leadingIcon={<Library size={14} aria-hidden="true" />}
              onClick={onOpenPicker}
            >
              {tr(S.chooseExisting)}
            </Button>
            {/* AI generation is images-only — audio can't be generated. */}
            {!audio ? (
              capabilities.imageGen ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  leadingIcon={<Wand2 size={14} aria-hidden="true" />}
                  onClick={onOpenGenerate}
                >
                  {tr(COMMON.generate)}
                </Button>
              ) : (
                <span className="lw-assets__gen-note" title={tr(S.genNoteTitle)}>
                  {tr(S.genNote)}
                </span>
              )
            ) : null}
          </>
        )}
      </div>
    </li>
  );
}
